import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  RotateCcw, 
  Trash2, 
  Edit2, 
  Search, 
  Loader2,
  AlertCircle,
  Check
} from 'lucide-react';
import { 
  db, 
  collection, 
  onSnapshot, 
  query, 
  where, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  serverTimestamp 
} from '../firebase';
import { notify } from '../services/notify';
import { cn } from '../lib/utils';
import { type UOM, convertUomQuantity } from '../lib/uom';

const getLocalAuthToken = () => localStorage.getItem('app_auth_token');
const isLocalSession = () => Boolean(getLocalAuthToken());
const getLocalApiHeaders = () => ({
  Authorization: `Bearer ${getLocalAuthToken()}`,
  'Content-Type': 'application/json'
});

export default function UOMView({ orgId, language = 'en' }: { orgId: string, language?: string }) {
  const [uoms, setUoms] = useState<UOM[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUom, setEditingUom] = useState<UOM | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    category: 'Unit',
    type: 'reference' as 'reference' | 'bigger' | 'smaller',
    ratio: 1,
    status: 'active' as 'active' | 'archived'
  });

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      setLoading(true);
      void fetch(`/api/organizations/${orgId}/uoms`, { headers: getLocalApiHeaders() })
        .then(async response => {
          const payload = await response.json().catch(() => null);
          if (!response.ok) throw new Error(payload?.error || 'Could not load units of measure');
          return payload as UOM[];
        })
        .then(units => {
          if (!cancelled) setUoms(units);
        })
        .catch(error => {
          console.error('Local API error loading UOMs:', error);
          if (!cancelled) notify.error(error instanceof Error ? error.message : 'Failed to load UOMs');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => { cancelled = true; };
    }

    const q = query(collection(db, 'uoms'), where('orgId', '==', orgId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setUoms(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as UOM)));
      setLoading(false);
    });
    return () => unsubscribe();
  }, [orgId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId) return;

    try {
      const payload = {
        ...formData,
        ratio: formData.type === 'reference' ? 1 : formData.ratio
      };

      if (isLocalSession()) {
        const endpoint = editingUom
          ? `/api/organizations/${orgId}/uoms/${editingUom.id}`
          : `/api/organizations/${orgId}/uoms`;
        const response = await fetch(endpoint, {
          method: editingUom ? 'PATCH' : 'POST',
          headers: getLocalApiHeaders(),
          body: JSON.stringify(payload)
        });
        const saved = await response.json().catch(() => null) as UOM | null;
        if (!response.ok || !saved) throw new Error((saved as any)?.error || 'Could not save unit of measure');
        setUoms(current => editingUom
          ? current.map(unit => unit.id === saved.id ? saved : unit)
          : [...current, saved].sort((left, right) => left.category.localeCompare(right.category) || left.name.localeCompare(right.name))
        );
      } else {
        const data = {
          ...payload,
          orgId,
          updatedAt: serverTimestamp()
        };
        if (editingUom) {
          await updateDoc(doc(db, 'uoms', editingUom.id), data);
        } else {
          await addDoc(collection(db, 'uoms'), {
            ...data,
            createdAt: serverTimestamp()
          });
        }
      }

      notify.confirm(editingUom ? "UOM updated successfully" : "UOM created successfully");
      setIsModalOpen(false);
      setEditingUom(null);
      setFormData({ name: '', category: 'Unit', type: 'reference', ratio: 1, status: 'active' });
    } catch (error) {
      console.error(error);
      notify.error(error instanceof Error ? error.message : "Failed to save UOM");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this UOM?")) return;
    try {
      if (isLocalSession()) {
        const response = await fetch(`/api/organizations/${orgId}/uoms/${id}`, {
          method: 'DELETE',
          headers: getLocalApiHeaders()
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          throw new Error(payload?.error || 'Could not delete unit of measure');
        }
        setUoms(current => current.filter(unit => unit.id !== id));
      } else {
        await deleteDoc(doc(db, 'uoms', id));
      }
      notify.confirm("UOM deleted");
    } catch (error) {
      console.error(error);
      notify.error(error instanceof Error ? error.message : "Failed to delete UOM");
    }
  };

  const filteredUoms = uoms.filter(u => 
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const categories = Array.from(new Set(uoms.map(u => u.category).concat(['Unit', 'Weight', 'Length', 'Volume', 'Time'])));

  return (
    <div className="p-6 space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold uppercase tracking-widest flex items-center gap-2">
            <RotateCcw className="w-5 h-5 opacity-40" />
            Units of Measure (UOM)
          </h2>
          <p className="text-xs opacity-50 mt-1 font-mono">Define measurement units and their conversion ratios</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white/50 border border-[#141414]/10 px-4 py-2 rounded-xl w-64">
            <Search className="w-4 h-4 opacity-30" />
            <input 
              type="text" 
              placeholder="Search UOMs..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none text-xs w-full font-bold"
            />
          </div>
          <button 
            onClick={() => {
              setEditingUom(null);
              setFormData({ name: '', category: 'Unit', type: 'reference', ratio: 1, status: 'active' });
              setIsModalOpen(true);
            }}
            className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-6 py-2.5 rounded-full hover:opacity-90 transition-all text-xs font-bold whitespace-nowrap shadow-xl"
          >
            <Plus className="w-4 h-4" />
            <span>Create UOM</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {loading ? (
          <div className="p-20 text-center"><Loader2 className="w-10 h-10 animate-spin mx-auto opacity-20" /></div>
        ) : filteredUoms.length === 0 ? (
          <div className="p-20 text-center bg-white/30 border-2 border-dashed border-[#141414]/10 rounded-3xl opacity-40 italic serif uppercase tracking-widest">
            No UOMs found. Define your first measurement unit.
          </div>
        ) : (
          <div className="bg-white/50 border border-[#141414] rounded-3xl overflow-hidden shadow-2xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse font-mono text-sm">
                <thead>
                  <tr className="border-b border-[#141414] bg-[#141414] text-[#E4E3E0]">
                    <th className="p-4 text-xs font-bold uppercase tracking-widest">Name</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-widest">Category</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-widest">Type</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-widest text-right">Ratio</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-widest">Formula</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-widest"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUoms.map(uom => {
                    const refUnit = uoms.find(ru => ru.category === uom.category && ru.type === 'reference');
                    return (
                      <tr key={uom.id} className="border-b border-[#141414]/10 hover:bg-[#141414]/5 transition-colors group">
                        <td className="p-4 font-bold text-[#141414]">{uom.name}</td>
                        <td className="p-4"><span className="bg-[#141414]/5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border border-[#141414]/10">{uom.category}</span></td>
                        <td className="p-4">
                           <span className={cn(
                             "px-2 py-0.5 rounded text-[10px] font-bold uppercase",
                             uom.type === 'reference' ? "bg-blue-100 text-blue-700" : 
                             uom.type === 'bigger' ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"
                           )}>
                             {uom.type}
                           </span>
                        </td>
                        <td className="p-4 text-right font-bold">{uom.ratio}</td>
                        <td className="p-4">
                          <p className="text-[10px] opacity-60">
                            {uom.type === 'reference' ? (
                              `1 ${uom.name} = Base`
                            ) : uom.type === 'bigger' ? (
                              `1 ${uom.name} = ${uom.ratio} ${refUnit?.name || 'Base'}`
                            ) : (
                              refUnit ? `1 ${refUnit.name} = ${convertUomQuantity(1, refUnit, uom)} ${uom.name}` : `1 Base = ${1 / uom.ratio} ${uom.name}`
                            )}
                          </p>
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button 
                              onClick={() => {
                                setEditingUom(uom);
                                setFormData({
                                  name: uom.name,
                                  category: uom.category,
                                  type: uom.type,
                                  ratio: uom.ratio,
                                  status: uom.status
                                });
                                setIsModalOpen(true);
                              }}
                              className="p-2 hover:bg-black/5 rounded-xl transition-all"
                            >
                              <Edit2 className="w-4 h-4 opacity-50" />
                            </button>
                            <button 
                              onClick={() => handleDelete(uom.id)}
                              className="p-2 hover:bg-red-50 text-red-500 rounded-xl transition-all"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsModalOpen(false);
          }}
        >
          <div className="bg-white w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-3xl border border-[#141414] shadow-2xl animate-in zoom-in duration-300">
            <div className="p-6 border-b border-[#141414]/10 flex items-center justify-between bg-[#141414] text-white">
              <h3 className="text-sm font-bold uppercase tracking-[0.2em]">{editingUom ? 'Edit Unit of Measure' : 'Create New UOM'}</h3>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="p-1 hover:bg-white/10 rounded-full transition-colors"
              >
                <Plus className="w-5 h-5 rotate-45" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase opacity-40 px-1">Unit Name</label>
                <input 
                  required
                  type="text" 
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g. Kilogram, Box, Litre"
                  className="w-full bg-[#141414]/5 border-none rounded-2xl px-4 py-3 text-sm font-bold focus:ring-2 focus:ring-[#141414]/20 outline-none transition-all"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase opacity-40 px-1">Category</label>
                <div className="relative group">
                  <select 
                    value={formData.category}
                    onChange={e => setFormData({...formData, category: e.target.value})}
                    className="w-full bg-[#141414]/5 border-none rounded-2xl px-4 py-3 text-sm font-bold focus:ring-2 focus:ring-[#141414]/20 outline-none appearance-none cursor-pointer"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    <option value="new">+ Create New Category</option>
                  </select>
                </div>
                {formData.category === 'new' && (
                  <input 
                    autoFocus
                    type="text" 
                    placeholder="Enter new category name..."
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const val = (e.currentTarget as HTMLInputElement).value.trim();
                        if (val) setFormData({...formData, category: val});
                      }
                    }}
                    className="w-full mt-2 bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3 text-sm font-bold outline-none"
                  />
                )}
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase opacity-40 px-1">Unit Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['reference', 'bigger', 'smaller'] as const).map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setFormData({...formData, type, ratio: type === 'reference' ? 1 : formData.ratio})}
                      className={cn(
                        "py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest border transition-all",
                        formData.type === type ? "bg-[#141414] text-white border-[#141414]" : "bg-white border-[#141414]/10 opacity-60"
                      )}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {formData.type !== 'reference' && (
                <div className="space-y-1.5 animate-in slide-in-from-top-2">
                  <label className="text-[10px] font-bold uppercase opacity-40 px-1">
                    {formData.type === 'bigger' ? 'Ratio to Reference Unit' : 'Ratio to Reference Unit (Fraction)'}
                  </label>
                  <div className="space-y-4">
                    <input 
                      required
                      type="number" 
                      step="any"
                      min="0.00000001"
                      value={formData.ratio}
                      onChange={e => setFormData({...formData, ratio: parseFloat(e.target.value) || 0})}
                      className="w-full bg-[#141414]/5 border-none rounded-2xl px-4 py-3 text-sm font-bold focus:ring-2 focus:ring-[#141414]/20 outline-none transition-all"
                    />
                    <div className="p-3 bg-blue-50 border border-blue-100 rounded-2xl flex gap-3 text-blue-900">
                      <AlertCircle className="w-5 h-5 shrink-0 opacity-40" />
                      <div className="text-[10px] leading-relaxed">
                        {formData.type === 'bigger' ? (
                          <p>Example: If 1 Dozen = 12 Units, Ratio is <strong>12</strong></p>
                        ) : (
                          <p>Example: If 1000 Grams = 1 Kilogram, Ratio is <strong>0.001</strong></p>
                        )}
                        <p className="mt-1 font-bold italic">Calculation: 1 {formData.name || 'this unit'} = {formData.ratio} reference units</p>
                        <p className="mt-1 opacity-70">Product conversions use this ratio only between units in the same category.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-6">
                <button 
                  type="submit"
                  className="w-full bg-[#141414] text-white py-4 rounded-2xl font-bold uppercase tracking-[0.2em] shadow-xl hover:opacity-90 active:scale-[0.98] transition-all"
                >
                  {editingUom ? 'Update Unit' : 'Save Unit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
